
export const processDetailHtml = (htmlString, options = {}) => {
  const { hideQr = false } = options;
  if (!htmlString || typeof htmlString !== 'string') return htmlString;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // 1. Clean up "Order Date" in <p> tags and other potential date containers
  const pTags = doc.querySelectorAll('p');
  pTags.forEach(p => {
    if (p.innerHTML.includes('Order Date')) {
      const parts = p.innerHTML.includes('Order Date :') 
        ? p.innerHTML.split('Order Date :') 
        : p.innerHTML.split('Order Date:');
        
      if (parts.length > 1) {
        const dateMatch = parts[1].match(/\d{1,2}[-/]\d{1,2}[-/]\d{2,4}(?:\s+\d{1,2}:\d{2}(?:\s*[APap][Mm])?)?/);
        if (dateMatch) {
            p.innerHTML = `${parts[0]}Order Date :</strong> ${dateMatch[0]}`;
        }
      }
    }
  });

  // 1.5. Swap bold formatting for metadata fields (labels normal, values bold)
  const headerParagraphs = doc.querySelectorAll('.header p');
  headerParagraphs.forEach(p => {
    const boldEl = p.querySelector('strong, b');
    if (boldEl) {
      const labelText = boldEl.textContent.trim();
      const labelLower = labelText.toLowerCase();
      
      if (
        labelLower.includes('order id') ||
        labelLower.includes('customer') ||
        labelLower.includes('name') ||
        labelLower.includes('contact') ||
        labelLower.includes('date')
      ) {
        const fullText = p.textContent;
        const valueText = fullText.replace(labelText, '').replace(/^\s*:\s*/, '').trim();
        
        if (valueText) {
          const cleanLabel = labelText.endsWith(':') ? labelText : `${labelText}:`;
          p.innerHTML = `${cleanLabel} <strong>${valueText}</strong>`;
        }
      }
    }
  });

  // 2. Clean up dates in Previous Pending Orders table (Column 2)
  const prevTable = doc.querySelector('.prev-orders-table');
  if (prevTable) {
    const rows = prevTable.querySelectorAll('tbody tr');
    rows.forEach(row => {
      const cells = row.querySelectorAll('td');
      if (cells.length >= 2) {
        const dateText = cells[1].textContent.trim();
        if (dateText) {
          const dateMatch = dateText.match(/\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/);
          if (dateMatch) {
              cells[1].textContent = dateMatch[0];
          }
        }
      }
    });
  }

  // 2.5. Tag the items table based on its column count to isolate styles
  const tables = doc.querySelectorAll('table');
  tables.forEach(table => {
    if (!table.classList.contains('prev-orders-table')) {
      const firstRow = table.querySelector('tr');
      if (firstRow) {
        const colCount = firstRow.querySelectorAll('th, td').length;
        table.classList.add(`cols-${colCount}`);
      }
    }
  });

  // 2.7. Locate and right-align all standalone total/charges lines (e.g., "Total ₹ 9.00")
  const blockElements = doc.querySelectorAll('p, div');
  blockElements.forEach(el => {
    const text = el.textContent.trim();
    if (/^(?:Total|Grand\s+Total|Total\s+Charges)\b/i.test(text)) {
      el.style.textAlign = 'right';
      el.style.fontWeight = 'bold';
      el.style.width = '100%';
      el.style.display = 'block';
    }
  });

  // 3. Inject styles to fix the "messy" layout and unify UI
  let styleTag = doc.querySelector('style');
  if (!styleTag) {
    styleTag = doc.createElement('style');
    doc.head.appendChild(styleTag);
  }

  // Wrap the body content in a container to scope styles
  const originalBody = doc.body.innerHTML;
  doc.body.innerHTML = `<div class="print-preview-container">${originalBody}</div>`;

  const additionalStyles = `
    /* Zero margin for actual printing only */
    @media print {
      @page { margin: 0; }
      body { margin: 0 !important; padding: 0 !important; width: 100% !important; }
      .print-preview-container { border: none !important; box-shadow: none !important; margin: 0 !important; }
    }

    /* Scoped container for the thermal roll layout */
    .print-preview-container {
        max-width: 250px !important;
        width: 100% !important;
        margin: 0 auto !important;
        padding: 10px !important;
        box-sizing: border-box !important;
        font-family: 'Times New Roman', serif !important;
        font-size: 10px !important;
        color: #000 !important;
        background: #fff !important;
    }

    /* Standardize all tables within the preview */
    .print-preview-container table {
        width: 100% !important;
        border-collapse: collapse !important;
        margin-top: 2px !important;
        margin-bottom: 2px !important;
        border: none !important;
    }
    
    ${hideQr ? '.print-preview-container img[alt="barcode"], .print-preview-container .barcode { display: none !important; }' : ''}
    
    .print-preview-container table th, 
    .print-preview-container table td {
        padding: 1px 2px !important;
        font-size: 10px !important; 
        line-height: 1.2 !important;
        text-align: left !important;
        vertical-align: top !important;
        word-break: break-word !important;
        overflow-wrap: break-word !important;
        color: #000 !important;
        border: none !important;
    }

    /* Section Separators */
    .print-preview-container thead tr { 
        border-bottom: none !important; 
    }

    /* Hide redundant HR tags that create "middle lines" */
    .print-preview-container hr {
        display: none !important;
    }

    /* Remove default borders on rows */
    .print-preview-container tbody tr,
    .print-preview-container tfoot tr {
        border: none !important;
    }

    .print-preview-container .total { 
        border-top: 1px solid #000 !important; 
        margin-top: 2px !important;
        padding-top: 2px !important;
        text-align: right !important;
    }

    /* Column specific alignment */
    .print-preview-container table th:last-child, 
    .print-preview-container table td:last-child {
        text-align: right !important;
        white-space: nowrap !important;
    }

    /* Fixed widths to prevent wrapping */
    .print-preview-container .prev-orders-table th:nth-child(1), 
    .print-preview-container .prev-orders-table td:nth-child(1) { width: 45px !important; } 

    /* 3-Column Items Table (Total Print: Item, Type, Charges) */
    .print-preview-container table.cols-3 th:nth-child(1), 
    .print-preview-container table.cols-3 td:nth-child(1) { 
        width: 120px !important; 
        text-align: left !important;
    }
    .print-preview-container table.cols-3 th:nth-child(2), 
    .print-preview-container table.cols-3 td:nth-child(2) { 
        width: 60px !important; 
        text-align: right !important;
    }
    .print-preview-container table.cols-3 th:nth-child(3), 
    .print-preview-container table.cols-3 td:nth-child(3) { 
        width: 50px !important; 
        text-align: right !important;
    }

    /* 4-Column Items Table (Detail Print: Sr., Items, Type, Amount) */
    .print-preview-container table.cols-4 th:nth-child(1), 
    .print-preview-container table.cols-4 td:nth-child(1) { 
        width: 25px !important; 
        text-align: left !important;
    }
    .print-preview-container table.cols-4 th:nth-child(2), 
    .print-preview-container table.cols-4 td:nth-child(2) { 
        width: 100px !important; 
        text-align: left !important;
    }
    .print-preview-container table.cols-4 th:nth-child(3), 
    .print-preview-container table.cols-4 td:nth-child(3) { 
        width: 50px !important; 
        text-align: right !important;
    }
    .print-preview-container table.cols-4 th:nth-child(4), 
    .print-preview-container table.cols-4 td:nth-child(4) { 
        width: 55px !important; 
        text-align: right !important;
    }

    /* Prevent "Go ld" wrapping in prev orders summary */
    .print-preview-container .prev-orders-table th:nth-child(3), 
    .print-preview-container .prev-orders-table td:nth-child(3) { min-width: 40px !important; } 
    
    /* Restrict title styling to direct child elements of preview or header */
    .print-preview-container > p:first-child,
    .print-preview-container .header > p:first-child,
    .print-preview-container .header > h1:first-child { 
        text-align: center !important; 
        font-weight: bold !important; 
        font-size: 14px !important; 
        text-transform: uppercase !important;
        margin-bottom: 2px !important;
        display: block !important;
    }

    .print-preview-container h1 { font-size: 14px !important; margin: 5px 0 !important; text-align: center !important; text-transform: uppercase !important; }
    .print-preview-container p { margin: 2px 0 !important; font-size: 10px !important; line-height: 1.2 !important; color: #000 !important; }
    
    .print-preview-container .total p, 
    .print-preview-container .footer p { margin: 2px 0 !important; font-weight: bold !important; font-size: 10px !important; line-height: 1.2 !important; }
    
    /* Utility to hide things if needed */
    .print-preview-container .no-print { display: none !important; }

    .print-preview-container hr { border: 0; border-top: 1px solid #000; margin: 5px 0; }
  `;

  styleTag.textContent = additionalStyles;

  return doc.documentElement.outerHTML;
};
